import type { Onboarding2PipelineStage, Onboarding2StageUi } from './onboarding2Model';
import type { VmsPanelShellCardTone } from '../../components/vms/VmsPanelShellCard';
import type { User } from '../../types';
import { canEditOnboardingRegisteredVendorForms, isVmsAdminUser } from '../../utils/vmsRole';
import {
  derivePartApplicability,
  isStandardPnbilDpiaCode,
  rowsForDpiaPart,
} from './dpiaPartModel';
import {
  screeningOutcomeFromDpiaRows,
  type DpiaScreeningOutcome,
} from '../../utils/dpiaScreeningOutcome';

export type OnboardingPipelineSummaryDto = {
  annexure: Onboarding2StageUi;
  dpia: Onboarding2StageUi;
  contract: Onboarding2StageUi;
  notification_regulator: Onboarding2StageUi;
  register_filling: Onboarding2StageUi;
  prerequisites_complete: boolean;
  all_complete: boolean;
  /** True when DPIA stage was applicable (not marked non applicable). */
  dpia_applicable?: boolean;
  /** Annexure 7 overall assessment; null when not set. */
  is_material?: boolean | null;
};

/** Register filling approved — vendor moves to main register. */
export function isOnboardingPipelineFullyComplete(
  summary: OnboardingPipelineSummaryDto | null | undefined
): boolean {
  return summary?.all_complete === true;
}

export function isOnboardingPrerequisitesComplete(
  summary: OnboardingPipelineSummaryDto | null | undefined
): boolean {
  return summary?.prerequisites_complete === true;
}

/** Register quick links — show DPIA button only when stage was applicable. */
export function isDpiaApplicableForRegister(
  summary: OnboardingPipelineSummaryDto | null | undefined
): boolean {
  return summary?.dpia_applicable === true;
}

/**
 * DPIA Part A fill opens only when the pipeline stage is explicitly Applicable.
 * Prefer loaded row data; fall back to cached pipeline summary while rows load.
 */
export function isDpiaPipelineStageApplicableForFilling(
  summary: OnboardingPipelineSummaryDto | null | undefined,
  rows?: Array<{ code: string; applicability: 'applicable' | 'not_applicable' | null }>
): boolean {
  if (rows && rows.length > 0) {
    return derivePartApplicability(rowsForDpiaPart(rows, 'a')) === 'applicable';
  }
  return summary?.dpia_applicable === true;
}

/** Block DPIA filling when non applicable or not selected yet (only applicable opens the gate). */
export function isDpiaFillingGateBlocked(
  summary: OnboardingPipelineSummaryDto | null | undefined,
  rows?: Array<{ code: string; applicability: 'applicable' | 'not_applicable' | null }>
): boolean {
  return !isDpiaPipelineStageApplicableForFilling(summary, rows);
}

export const ONBOARDING_COMPLETE_READ_ONLY_MESSAGE =
  'Onboarding is complete for this vendor — forms are read-only. Use Admin Config → Register to manage the live vendor record.';

/** BO may edit onboarding forms only while register filling is not yet approved (vendor not live). */
export function canMutateOnboardingRegisteredVendorForms(
  user: User | null | undefined,
  pipelineSummary: OnboardingPipelineSummaryDto | null | undefined
): boolean {
  return (
    canEditOnboardingRegisteredVendorForms(user) && !isOnboardingPipelineFullyComplete(pipelineSummary)
  );
}

export const REGISTER_FILLING_PREREQUISITE_MESSAGE =
  'Complete annexure filling, DPIA filling, and contract review before register filling.';

/** Material vendors must also complete notification regulator (Annexure 7). */
export function registerFillingPrerequisiteMessage(isMaterial: boolean | null | undefined): string {
  if (isMaterial === true) {
    return 'Complete annexure filling, DPIA filling, contract review, and notification regulator before register filling.';
  }
  return REGISTER_FILLING_PREREQUISITE_MESSAGE;
}

export function registerFillingApproverBlockedMessage(isMaterial: boolean | null | undefined): string {
  if (isMaterial === true) {
    return 'Onboarding steps are not complete — approval is blocked until annexure filling, DPIA filling, contract review, and notification regulator are finished.';
  }
  return 'Onboarding steps are not complete — approval is blocked until annexure filling, DPIA filling, and contract review are finished.';
}

export function pipelineSummaryToStageUi(
  summary: OnboardingPipelineSummaryDto
): Record<Onboarding2PipelineStage, Onboarding2StageUi> {
  return {
    annexure: summary.annexure,
    dpia: summary.dpia,
    contract: summary.contract,
  };
}

/** Map pipeline step UI state to hub card shell tones (green / yellow / pink / grey). */
export function pipelineStageUiToCardTone(
  state: Onboarding2StageUi,
  dpiaStageChoice?: DpiaPipelineStageChoice,
): VmsPanelShellCardTone {
  if (dpiaStageChoice === 'not_applicable') return 'not_applicable';
  if (state === 'done') return 'approved';
  if (state === 'current') return 'pending_approval';
  return 'default';
}

/** Only these annexures are part of onboarding annexure filling UI. */
export const ONBOARDING_VISIBLE_ANNEXURE_CODES = new Set(['4', '5', '6', '8', '9', '13', '15A', '15B']);

export function isOnboardingVisibleAnnexureCode(code: unknown): boolean {
  return ONBOARDING_VISIBLE_ANNEXURE_CODES.has(String(code ?? '').trim().toUpperCase());
}

type ApplicableFormItem = {
  applicability?: 'applicable' | 'not_applicable' | null;
  status?: string;
  configured_approver_ids?: number[];
  not_applicable_reason?: string | null;
};

const DPIA_STAGE_NOT_APPLICABLE_BLOCKING_STATUSES = new Set([
  'pending_approval',
  'needs_more_information',
]);

export type OnboardingFormRow = {
  code?: string;
  item?: ApplicableFormItem | null;
};

export type DpiaPipelineStageChoice = '' | 'applicable' | 'not_applicable';

/** Notification regulator pipeline dropdown value (same options as DPIA stage). */
export function deriveNotificationRegulatorApplicability(
  item: { applicability?: 'applicable' | 'not_applicable' | null } | null | undefined
): DpiaPipelineStageChoice {
  const a = item?.applicability;
  if (a === 'applicable' || a === 'not_applicable') return a;
  return '';
}

/** Only VMS admin may set NR applicability on the pipeline. */
export function canEditNotificationRegulatorApplicability(
  user: User | null | undefined,
  pipelineSummary: OnboardingPipelineSummaryDto | null | undefined,
  item: { status?: string; is_complete?: boolean } | null | undefined
): boolean {
  if (!isVmsAdminUser(user)) return false;
  if (!canMutateOnboardingRegisteredVendorForms(user, pipelineSummary)) return false;
  if (item?.is_complete) return false;
  const st = String(item?.status ?? 'draft');
  return st !== 'pending_approval' && st !== 'submitted';
}

const DPIA_PART_A_PRIMARY = 'PNBIL-D01';

/** True when the API returned at least one standard PNBIL DPIA definition row. */
export function hasDpiaPipelineDefinitions(rows: OnboardingFormRow[]): boolean {
  return mapDpiaPipelineRows(rows).length > 0;
}

function mapDpiaPipelineRows(rows: OnboardingFormRow[]): Array<{
  code: string;
  applicability: 'applicable' | 'not_applicable' | null;
  item: { status?: string; response?: Record<string, unknown> | null } | null;
}> {
  return rows
    .filter((r) => isStandardPnbilDpiaCode(String(r.code ?? '')))
    .map((r) => ({
      code: String(r.code),
      applicability: r.item?.applicability ?? null,
      item: r.item
        ? {
            status: r.item.status,
            response: (r.item as { response?: Record<string, unknown> | null }).response ?? null,
          }
        : null,
    }));
}

/**
 * Unified DPIA pipeline step — one DPIA filling workflow (Part A submit/approve covers inline Part B).
 */
export function computeDpiaPipelineStageUi(
  rows: OnboardingFormRow[],
  _screeningOutcome?: DpiaScreeningOutcome | null
): Onboarding2StageUi {
  const mapped = mapDpiaPipelineRows(rows);
  if (mapped.length === 0) return 'pending';

  const partAApp = derivePartApplicability(rowsForDpiaPart(mapped, 'a'));
  if (partAApp === 'not_applicable') return 'done';
  if (partAApp !== 'applicable') return 'pending';

  const primary = mapped.find((r) => String(r.code).trim().toUpperCase() === DPIA_PART_A_PRIMARY);
  const partAStatus = String(primary?.item?.status ?? 'draft');

  if (partAStatus === 'approved') return 'done';
  if (partAStatus === 'pending_approval' || partAStatus === 'needs_more_information') {
    return 'current';
  }
  return 'pending';
}

/** DPIA filling is complete when marked not applicable or the primary workflow row is approved. */
export function isDpiaFillingApproved(rows: OnboardingFormRow[]): boolean {
  return computeDpiaPipelineStageUi(rows) === 'done';
}

/** Only VMS admin may change the DPIA pipeline Applicable / Non applicable dropdown. */
export function canEditDpiaPipelineStageDropdown(
  user: User | null | undefined,
  pipelineSummary: OnboardingPipelineSummaryDto | null | undefined,
  dpiaRows: OnboardingFormRow[]
): boolean {
  if (!isVmsAdminUser(user)) return false;
  if (!canMutateOnboardingRegisteredVendorForms(user, pipelineSummary)) {
    return false;
  }
  // Non-applicable still counts as pipeline "done" but admin may switch back to applicable.
  if (deriveDpiaPipelineStageChoice(dpiaRows) === 'not_applicable') {
    return true;
  }
  const mapped = mapDpiaPipelineRows(dpiaRows);
  const primary = mapped.find((r) => String(r.code).trim().toUpperCase() === DPIA_PART_A_PRIMARY);
  const partAStatus = String(primary?.item?.status ?? 'draft');
  return partAStatus !== 'approved';
}

export function canMarkDpiaStageNotApplicable(
  rows: OnboardingFormRow[],
  screeningOutcome?: DpiaScreeningOutcome | null
): { ok: true } | { ok: false; message: string } {
  const mapped = mapDpiaPipelineRows(rows);
  const outcome = screeningOutcome ?? screeningOutcomeFromDpiaRows(mapped);
  if (outcome.partBRequired || outcome.partBMandatory || outcome.dpiaRequired) {
    return {
      ok: false,
      message:
        'Cannot mark DPIA as not applicable when the screening assessment requires a full DPIA.',
    };
  }
  const primary = mapped.find((r) => String(r.code).trim().toUpperCase() === DPIA_PART_A_PRIMARY);
  const st = String(primary?.item?.status ?? 'draft');
  if (st === 'pending_approval' || st === 'needs_more_information' || st === 'approved') {
    return {
      ok: false,
      message:
        'Cannot mark DPIA as not applicable while forms are pending approval or already approved.',
    };
  }
  return { ok: true };
}

export function screeningOutcomeFromOnboardingDpiaRows(
  rows: OnboardingFormRow[]
): DpiaScreeningOutcome {
  return screeningOutcomeFromDpiaRows(mapDpiaPipelineRows(rows));
}

function isAnnexureConfigurationComplete(rows: OnboardingFormRow[]): boolean {
  return (
    rows.length > 0 &&
    rows.every((r) => {
      const app = r.item?.applicability;
      return app === 'applicable' || app === 'not_applicable';
    })
  );
}

function applicableFormRows<T extends OnboardingFormRow>(rows: T[]): T[] {
  return rows.filter((r) => r.item?.applicability === 'applicable');
}

/**
 * Annexure / DPIA pipeline step: complete when configuration is saved and every applicable form is approved.
 */
export function computeApplicableFormsStageUi(rows: OnboardingFormRow[]): Onboarding2StageUi {
  if (rows.length === 0 || !isAnnexureConfigurationComplete(rows)) return 'pending';

  const applicable = applicableFormRows(rows);
  if (applicable.length === 0) return 'done';

  const allApproved = applicable.every((r) => (r.item?.status ?? 'draft') === 'approved');
  if (allApproved) return 'done';

  const anyInWorkflow = applicable.some((r) => {
    const st = r.item?.status ?? 'draft';
    return st !== 'draft';
  });
  return anyInWorkflow ? 'current' : 'pending';
}

/** Unified contract review: approved → done; otherwise in workflow or not started. */
export function computeContractReviewStageUi(reviewStatus: string | undefined): Onboarding2StageUi {
  if (reviewStatus === 'approved') return 'done';
  if (reviewStatus === 'pending_approval' || reviewStatus === 'needs_more_information') return 'current';
  if (reviewStatus && reviewStatus !== 'draft') return 'current';
  return 'pending';
}

export function unwrapAnnexureRows(raw: unknown): OnboardingFormRow[] {
  if (raw == null || typeof raw !== 'object') return [];
  const o = raw as Record<string, unknown>;
  const inner =
    o.annexures != null
      ? o
      : o.data != null && typeof o.data === 'object' && !Array.isArray(o.data)
        ? (o.data as Record<string, unknown>)
        : o;
  const annexures = Array.isArray(inner.annexures) ? inner.annexures : [];
  return annexures as OnboardingFormRow[];
}

export function unwrapDpiaRows(raw: unknown): OnboardingFormRow[] {
  return unwrapDpiaRowsWithCode(raw);
}

export function unwrapDpiaRowsWithCode(raw: unknown): OnboardingFormRow[] {
  if (raw == null || typeof raw !== 'object') return [];
  const o = raw as Record<string, unknown>;
  const inner =
    o.dpia_forms != null
      ? o
      : o.data != null && typeof o.data === 'object' && !Array.isArray(o.data)
        ? (o.data as Record<string, unknown>)
        : o;
  const forms = Array.isArray(inner.dpia_forms) ? inner.dpia_forms : [];
  return forms.map((row) => {
    const r = row as OnboardingFormRow & { code?: unknown };
    return {
      code: r.code != null ? String(r.code) : undefined,
      item: r.item ?? null,
    };
  });
}

/** Forms that block marking the whole DPIA stage as non applicable. */
export function dpiaRowsBlockingStageNotApplicable(rows: OnboardingFormRow[]): OnboardingFormRow[] {
  return rows.filter((r) => {
    const st = String(r.item?.status ?? 'draft').toLowerCase();
    return DPIA_STAGE_NOT_APPLICABLE_BLOCKING_STATUSES.has(st);
  });
}

export function readDpiaRowApproverIds(row: OnboardingFormRow): number[] {
  const raw = row.item?.configured_approver_ids;
  if (!Array.isArray(raw)) return [];
  return raw.filter((id) => Number.isInteger(id) && id > 0);
}

/** Pipeline DPIA card dropdown: whole-stage applicable / not applicable (not per subsection). */
export function deriveDpiaPipelineStageChoice(rows: OnboardingFormRow[]): DpiaPipelineStageChoice {
  const mapped = mapDpiaPipelineRows(rows);
  if (mapped.length === 0) return '';

  const partAApp = derivePartApplicability(rowsForDpiaPart(mapped, 'a'));
  if (partAApp === 'not_applicable') return 'not_applicable';
  if (partAApp === 'applicable') return 'applicable';

  const primary = mapped.find((r) => String(r.code).trim().toUpperCase() === DPIA_PART_A_PRIMARY);
  const app = primary?.applicability ?? null;
  if (app === 'not_applicable') return 'not_applicable';
  if (app === 'applicable') return 'applicable';
  return '';
}

export function applyDpiaApplicabilityPatchToRows(
  rows: OnboardingFormRow[],
  patchItems: Array<{
    code: string;
    applicability: 'applicable' | 'not_applicable';
    approver_ids: number[];
    not_applicable_reason?: string | null;
  }>
): OnboardingFormRow[] {
  const byCode = new Map(patchItems.map((p) => [p.code, p]));
  return rows.map((r) => {
    const code = r.code != null ? String(r.code) : '';
    const patch = byCode.get(code);
    if (!patch) return r;
    const item = r.item ?? {};
    return {
      ...r,
      item: {
        ...item,
        applicability: patch.applicability,
        configured_approver_ids: patch.approver_ids,
        status: item.status ?? 'draft',
        ...(Object.prototype.hasOwnProperty.call(patch, 'not_applicable_reason')
          ? { not_applicable_reason: patch.not_applicable_reason ?? null }
          : patch.applicability === 'applicable'
            ? { not_applicable_reason: null }
            : {}),
      },
    };
  });
}

/** First non-empty not_applicable_reason among DPIA rows marked not applicable. */
export function deriveDpiaStageNotApplicableReason(rows: OnboardingFormRow[]): string {
  for (const r of rows) {
    const app = r.item?.applicability;
    if (app !== 'not_applicable') continue;
    const reason = r.item?.not_applicable_reason;
    if (reason != null && String(reason).trim() !== '') return String(reason);
  }
  return '';
}

/**
 * Patch items to update not_applicable_reason on existing not_applicable DPIA rows
 * (e.g. admin edits reason after stage was already marked N/A).
 */
export function buildDpiaNotApplicableReasonPatchItems(
  rows: OnboardingFormRow[],
  reason: string | null
): Array<{
  code: string;
  applicability: 'not_applicable';
  approver_ids: number[];
  not_applicable_reason: string | null;
}> {
  return rows
    .filter((r) => {
      if (!r.code?.trim() || !isStandardPnbilDpiaCode(r.code)) return false;
      if (r.item?.applicability !== 'not_applicable') return false;
      const st = String(r.item?.status ?? 'draft').toLowerCase();
      return st === 'draft' || st === 'needs_more_information' || r.item == null;
    })
    .map((r) => ({
      code: String(r.code),
      applicability: 'not_applicable' as const,
      approver_ids: [],
      not_applicable_reason: reason,
    }));
}

export function buildDpiaStageApplicabilityPatchItems(
  rows: OnboardingFormRow[],
  stageChoice: Exclude<DpiaPipelineStageChoice, ''>,
  notApplicableReason?: string | null
): Array<{
  code: string;
  applicability: 'applicable' | 'not_applicable';
  approver_ids: number[];
  not_applicable_reason?: string | null;
}> {
  const applicability = stageChoice;
  const codesToPatch =
    stageChoice === 'applicable'
      ? rowsForDpiaPart(mapDpiaPipelineRows(rows), 'a').map((r) => r.code)
      : mapDpiaPipelineRows(rows).map((r) => r.code);

  return rows
    .filter((r) => {
      if (!r.code?.trim() || !isStandardPnbilDpiaCode(r.code)) return false;
      if (!codesToPatch.includes(String(r.code))) return false;
      const st = String(r.item?.status ?? 'draft').toLowerCase();
      if (st === 'approved') return false;
      const app = r.item?.applicability ?? null;
      if (applicability === 'not_applicable') {
        return app !== 'not_applicable';
      }
      return app === 'not_applicable' || app == null;
    })
    .map((r) => ({
      code: String(r.code),
      applicability,
      approver_ids: readDpiaRowApproverIds(r),
      not_applicable_reason:
        applicability === 'not_applicable' ? notApplicableReason ?? null : null,
    }));
}

export function unwrapUnifiedContractReviewStatus(raw: unknown): string | undefined {
  if (raw == null || typeof raw !== 'object') return undefined;
  const o = raw as Record<string, unknown>;
  const inner =
    o.review != null
      ? o
      : o.data != null && typeof o.data === 'object' && !Array.isArray(o.data)
        ? (o.data as Record<string, unknown>)
        : o;
  const review = inner.review;
  if (review == null || typeof review !== 'object') return undefined;
  const status = (review as Record<string, unknown>).status;
  return status != null ? String(status) : undefined;
}
